import React from 'react';
import { useState, useEffect } from 'react';
import {formatCurrency} from '../../../../../../../../utils/functions';
import { SalesStatistics} from './../../../../types';
import { isSuperAdmin } from '../../../../../../../../utils/functions';
import { FaTrash, FaChevronDown, FaChevronUp, FaClock, FaFileInvoice } from 'react-icons/fa';
import DeleteSession from './components/DeleteSession/DeleteSession';
import SessionBriefModal from '../SessionBriefModal/SessionBriefModal';


interface TableProps {
    data : SalesStatistics[];
}

const Table: React.FC<TableProps> = ({ data }) => {
  const [currentData, setCurrentData] = React.useState<SalesStatistics[]>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [sessionsAfterMap, setSessionsAfterMap] = useState<Record<number, number>>({});
  const [expandedSessions, setExpandedSessions] = useState<Set<number>>(new Set());
  const [showSessionBrief, setShowSessionBrief] = useState(false);
  const [briefSessionId, setBriefSessionId] = useState<number | null>(null);
  const [briefSessionPeriod, setBriefSessionPeriod] = useState<{start: string; end: string} | undefined>(undefined);

    useEffect(() => {
        // Filter and sort by session_id DESC (higher session_id = most recent)
        const filtered = data.filter(row => row.net_sales > 0);
        const sorted = [...filtered].sort((a, b) => {
          const sessionIdA = (a as any).session_id ?? 0;
          const sessionIdB = (b as any).session_id ?? 0;
          return sessionIdB - sessionIdA; // DESC order
        });
        setCurrentData(sorted);

        // Calcular cuántas sesiones hay después de cada una usando todo el dataset
        const ordered = [...data].sort((a,b) => {
          const ta = new Date(a.start_timestamp).getTime();
          const tb = new Date(b.start_timestamp).getTime();
          if (ta !== tb) return ta - tb;
          return a.sales_total_id - b.sales_total_id; // estable
        });
        const indexById: Record<number, number> = {};
        ordered.forEach((row, idx) => { indexById[row.sales_total_id] = idx; });
        const afterMap: Record<number, number> = {};
        ordered.forEach((row, idx) => {
          afterMap[row.sales_total_id] = ordered.length - idx - 1;
        });
        setSessionsAfterMap(afterMap);
      }, [data]);
    
    const openDeleteModal = (sessionId: number) => {
      setSelectedSessionId(sessionId);
      setShowDelete(true);
    };

    const handleDeleted = () => {
      // Optimistically remove the deleted session from the table
      if (selectedSessionId !== null) {
        setCurrentData(prev => prev.filter(r => r.local_session_id !== selectedSessionId));
      }
    };

    const toggleShiftDetails = (sessionId: number) => {
      setExpandedSessions(prev => {
        const newSet = new Set(prev);
        if (newSet.has(sessionId)) {
          newSet.delete(sessionId);
        } else {
          newSet.add(sessionId);
        }
        return newSet;
      });
    };

    const handleOpenSessionBrief = (row: SalesStatistics) => {
      const sessionId = (row as any).session_id ?? (row as any).local_session_id;
      setBriefSessionId(sessionId);
      setBriefSessionPeriod({
        start: row.start_timestamp,
        end: row.end_timestamp
      });
      setShowSessionBrief(true);
    };

    const renderTable = () => {
      return (
        <div className="flex flex-col gap-2">
          {currentData.map(row => {
            const id = (row as any).local_session_id ?? (row as any).session_id ?? null;
            const sessionsAfter = sessionsAfterMap[row.sales_total_id] ?? 0;
            const tooManyAfter = sessionsAfter > 6;
            const blockedReason = id === null
              ? 'No se puede eliminar: ID de sesión no disponible.'
              : tooManyAfter
                ? `No se puede eliminar: tiene ${sessionsAfter} sesiones posteriores (máximo permitido: 6).`
                : null;
            const handleClick = () => {
              if (blockedReason) {
                window.alert(blockedReason);
                return;
              }
              openDeleteModal(id as number);
            };
            // Format user-friendly dates
            const formatFriendlyDate = (dateStr: string) => {
              const date = new Date(dateStr);
              const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                             'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
              const day = date.getDate();
              const month = months[date.getMonth()];
              const year = date.getFullYear();
              const hours = date.getHours().toString().padStart(2, '0');
              const minutes = date.getMinutes().toString().padStart(2, '0');
              return `${day} de ${month}, ${year} a las ${hours}:${minutes} hrs`;
            };

            const sessionId = (row as any).session_id ?? row.sales_total_id;
            const isExpanded = expandedSessions.has(sessionId);
            const rowWithShifts = row as any;
            const hasShifts = rowWithShifts.shifts && Object.keys(rowWithShifts.shifts).length > 0;

            return (
              <div className="mp-session-card" key={row.sales_total_id}>
                <div className="mp-session-header">
                  <div className="flex items-center gap-3 flex-wrap min-w-0">
                    <span className="mp-session-badge">{formatCurrency(row.net_sales)}</span>
                    <span className="mp-session-period">
                      Desde el {formatFriendlyDate(row.start_timestamp)} hasta el {formatFriendlyDate(row.end_timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="mp-btn-icon" onClick={() => handleOpenSessionBrief(row)} title="Ver resumen de sesión">
                      <FaFileInvoice />
                    </button>
                    {isSuperAdmin() && (
                      <button
                        className={`mp-btn-icon ${blockedReason ? 'opacity-30 cursor-not-allowed' : ''}`}
                        aria-disabled={!!blockedReason}
                        title={blockedReason ?? 'Eliminar sesión'}
                        onClick={handleClick}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mp-session-metrics">
                  <div className="flex flex-col"><span className="mp-session-metric-label">Total</span><span className="mp-session-metric-value">{formatCurrency(row.gross_sales)}</span></div>
                  <div className="flex flex-col"><span className="mp-session-metric-label">Propina</span><span className="mp-session-metric-value">{formatCurrency(row.total_tip)}</span></div>
                  <div className="flex flex-col"><span className="mp-session-metric-label">Ticket Promedio</span><span className="mp-session-metric-value">{formatCurrency(row.avg_ticket)}</span></div>
                  <div className="flex flex-col"><span className="mp-session-metric-label">Pérdida Neta</span><span className="mp-session-metric-value" style={row.net_loss > 0 ? { color: '#EF4444' } : undefined}>{formatCurrency(row.net_loss)}</span></div>
                  <div className="flex-1" />
                  <div className="flex flex-col"><span className="mp-session-metric-label">Recibos</span><span className="mp-session-metric-value">{row.valid_receipt_count}</span></div>
                  <div className="flex flex-col"><span className="mp-session-metric-label">Usuario</span><span className="mp-session-metric-value">{row.user_name ?? '—'}</span></div>
                </div>

                {hasShifts && (
                  <>
                    <button
                      className="flex items-center gap-2 w-full mt-3 px-3 py-2 rounded-lg text-xs font-semibold border-none cursor-pointer"
                      style={{ background: 'rgba(252,117,86,0.05)', border: '1px solid rgba(252,117,86,0.15)', color: '#FC7556' }}
                      onClick={() => toggleShiftDetails(sessionId)}
                    >
                      <FaClock />
                      <span>Ver detalle por turno</span>
                      <span className="ml-auto">{isExpanded ? <FaChevronUp /> : <FaChevronDown />}</span>
                    </button>

                    {isExpanded && (
                      <div className="flex flex-col gap-2 mt-3 p-3 rounded-lg" style={{ background: 'var(--background-color)' }}>
                        <div className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: 'var(--text-color)', opacity: 0.6 }}>
                          <FaClock /> Turnos de esta sesión
                        </div>
                        {Object.values(rowWithShifts.shifts).map((shift: any) => {
                          const duration = shift.end_time
                            ? ((new Date(shift.end_time).getTime() - new Date(shift.start_time).getTime()) / (1000 * 60 * 60)).toFixed(1)
                            : 'En curso';
                          return (
                            <div key={shift.shift_id} className="mp-stat-card flex-col !items-stretch !gap-2">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>{shift.name}</span>
                                  <span className={`mp-badge-${shift.active ? 'success' : 'neutral'}`} style={{ fontSize: '10px', padding: '1px 6px' }}>
                                    {shift.active ? 'Activo' : 'Cerrado'}
                                  </span>
                                </div>
                                <span className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.5 }}>
                                  {new Date(shift.start_time).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                  {shift.end_time && ` — ${new Date(shift.end_time).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                                  {' '}• {duration}h
                                </span>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="flex flex-col"><span className="mp-session-metric-label">Ventas Netas</span><span className="mp-session-metric-value" style={{ color: '#FC7556', fontWeight: 700 }}>{formatCurrency(shift.net_sales)}</span></div>
                                <div className="flex flex-col"><span className="mp-session-metric-label">Propinas</span><span className="mp-session-metric-value">{formatCurrency(shift.total_tip)}</span></div>
                                <div className="flex flex-col"><span className="mp-session-metric-label">Ticket Prom.</span><span className="mp-session-metric-value">{formatCurrency(shift.avg_ticket)}</span></div>
                                <div className="flex flex-col"><span className="mp-session-metric-label">Recibos</span><span className="mp-session-metric-value">{shift.valid_receipt_count}</span></div>
                              </div>
                              <div className="text-xs" style={{ color: 'var(--text-color)', opacity: 0.4 }}>
                                Apertura: {shift.open_user_name}
                                {shift.close_user_name && ` • Cierre: ${shift.close_user_name}`}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      );
    }


      const renderNoData = () => (
        <div className="mp-empty-state">
          <div className="mp-empty-state-title">No hay ningún movimiento aún</div>
        </div>
      );
    
    
      return (
       <div>
          {currentData.length > 0 ? renderTable() : renderNoData()}
          <DeleteSession
            show={showDelete}
            onClose={() => setShowDelete(false)}
            localSessionId={selectedSessionId}
            onDeleteSuccess={handleDeleted}
          />
          <SessionBriefModal
            show={showSessionBrief}
            onHide={() => setShowSessionBrief(false)}
            sessionId={briefSessionId ?? undefined}
            sessionPeriod={briefSessionPeriod}
          />
        </div>
      );
    };
    
    export default Table;